// The measurement logs (docs/PEDAGOGY.md §5.3, phase 1): the extra fields a review_log row's `log`
// carries, and the rows of the three measurement tables (db/schema/160-measurement.sql) as the
// API takes and returns them. Pure: shared by the app (writing), the Worker (validating) and the
// report (reading, lift.ts).
//
// Measured from what the learners already do; nothing here asks them anything except the one
// optional tap (session_rating).
import type { StressWordResult } from './stress-focus';
import { isActivityId, strategyOf, type ActivityId, type Rating, type SessionKind, type StrategyTag } from './strategies';

/** The app's own verdict on a typed answer (app/src/lib/answer.ts); 'empty' = "En tiedä". */
export type TypedVerdict = 'ok' | 'near' | 'wrong' | 'empty';

/**
 * Fields merged into a review_log row's `log` JSON (GradeArgs.logExtra, the exercise and listen
 * rows). All optional: rows written before phase 1 have none of them.
 */
export interface MeasureLog {
  strategy?: StrategyTag;
  activity?: ActivityId;
  /** Typed cards and exercises: the app's verdict. */
  verdict?: TypedVerdict | 'correct' | 'close';
  /** Response time, shown → answered or revealed (ms); absent when the page was hidden meanwhile. */
  ms?: number;
  /** FSRS retrievability just before the grade (the predicted recall, 0..1); review cards only. */
  r?: number;
  /** Lemma ids the item showed, asked or played (words touched; capped at MAX_LEMMAS). */
  lemmas?: string[];
  /**
   * Typed cards whose verdict was near or wrong: what was typed (at most MAX_TYPED characters). Omat
   * virheet (own-errors.ts) reads mistakes from it; review_log is private to its person.
   */
  typed?: string;
}

/** Response times above this are not a response time (the phone was put down): not logged. */
export const MAX_RESPONSE_MS = 10 * 60_000;
/** A typed answer is kept up to this length in the log (a card answer is one word or form). */
export const MAX_TYPED = 80;
/** Words touched per row at most (a log row stays small: the sync caps a row's JSON at 8000 chars). */
export const MAX_LEMMAS = 40;

/** The log fields of one card grade. */
export function cardMeasure(a: {
  activity: ActivityId;
  verdict?: TypedVerdict | null;
  ms?: number | null;
  r?: number | null;
  typed?: string | null;
}): MeasureLog {
  const out: MeasureLog = { strategy: strategyOf(a.activity), activity: a.activity };
  if (a.verdict) out.verdict = a.verdict;
  // the answer itself only when it was a mistake (a right answer is the card's own form)
  const typed = a.typed?.normalize('NFC').trim();
  if (typed && (a.verdict === 'near' || a.verdict === 'wrong')) out.typed = typed.slice(0, MAX_TYPED);
  if (typeof a.ms === 'number' && Number.isFinite(a.ms) && a.ms >= 0 && a.ms <= MAX_RESPONSE_MS) out.ms = Math.round(a.ms);
  if (typeof a.r === 'number' && Number.isFinite(a.r) && a.r > 0 && a.r <= 1) out.r = Math.round(a.r * 1000) / 1000;
  return out;
}

/** Unique non-empty lemma ids, in order, capped. */
export function touchedLemmas(ids: Iterable<string | undefined | null>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_LEMMAS) break;
  }
  return out;
}

// ---------- the measurement tables (API contract) ----------

/** POST /api/measure/activity-time: one stretch of time spent in one activity. */
export interface ActivityTimeEntry {
  /** Client uuid (a resend changes nothing). */
  id: string;
  activity: ActivityId;
  strategy: StrategyTag;
  /** ISO time the stretch started. */
  startedAt: string;
  /** Active seconds (input gaps capped, hidden time not counted). */
  seconds: number;
}

/** POST /api/measure/session-rating: the one tap (PEDAGOGY §5.4). The same id again changes it. */
export interface SessionRatingEntry {
  id: string;
  kind: SessionKind;
  /** YYYY-MM-DD (the person's local day). */
  day: string;
  /** The strategies the session contained. */
  strategies: StrategyTag[];
  rating: Rating;
  at: string;
}

/** POST /api/measure/recording-score: the summary of one analysed take (PEDAGOGY §5.3). */
export interface RecordingScoreEntry {
  id: string;
  /** The recording target ("lemma:ābra", "dialogue:…", a line id …). */
  target: string;
  /** The HV text recorded. */
  hv: string;
  activity: ActivityId;
  strategy: StrategyTag;
  /** What the long-vowel meter compared with: Peterson's clip of this text, his average, practice. */
  mode: 'reference' | 'peterson' | 'practice' | null;
  /** Words whose stress was checked (verdict ok or wrong) and those on target. */
  stressChecked: number;
  stressOk: number;
  /** Of the wrong ones, Finnish first-syllable stress. */
  stressInitial: number;
  /** Long vowels judged (verdict not unsure) and those long enough. */
  longChecked: number;
  longOk: number;
  /** Lemma ids of the recording (a lemma target), for the words-touched measure. */
  lemmas: string[];
  at: string;
  // ---- per-person stress feedback (pedagogy/stress-focus.ts, stress_word; 2026-10-09) ----
  /** YYYY-MM-DD, the person's local day of the take (with `words`). */
  day?: string;
  /** Per word: on target or not; only with mode 'reference' (Peterson's clip of the same text). */
  words?: StressWordResult[];
}

/** GET /api/measure: both people's rows since a time (the report reads them; decision 9). */
export interface MeasureRows {
  activityTime: (ActivityTimeEntry & { userId: string })[];
  ratings: (SessionRatingEntry & { userId: string })[];
  recordingScores: (RecordingScoreEntry & { userId: string })[];
}

// ---------- reading the logs back (the report, lift.ts) ----------

/** One review_log row as D1 returns it (log is the JSON text). */
export interface ReviewLogRow {
  user_id: string;
  item_id: string;
  type: string;
  grade: number;
  mode: string;
  reviewed_at: string;
  log: string;
}

const parse = (s: string): Record<string, unknown> => {
  try {
    const v: unknown = JSON.parse(s);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

/** A card review the lift can use: a word card in review state with the predicted recall logged. */
export interface ReviewSample {
  userId: string;
  /** The word (a form drill's item id without its "#form"). */
  lemmaId: string;
  cardType: string;
  at: string;
  /** Recalled = any grade but Uudelleen. */
  recalled: boolean;
  /** FSRS's predicted recall just before the grade. */
  predicted: number;
}

/** ts-fsrs State.Review: the card's state before the grade (a learning step is not a recall test). */
const FSRS_REVIEW = 2;

export function reviewSample(row: ReviewLogRow): ReviewSample | null {
  if (row.mode !== 'daily' && row.mode !== 'free') return null;
  if (row.type === 'sentence') return null;
  // (free practice on a card that was not due, log.practice, is still a recall test against
  // FSRS's prediction: kept)
  const log = parse(row.log);
  if (log.state !== FSRS_REVIEW) return null;
  const r = log.r;
  if (typeof r !== 'number' || !(r > 0 && r <= 1)) return null;
  return {
    userId: row.user_id,
    lemmaId: row.item_id.split('#')[0]!,
    cardType: row.type,
    at: row.reviewed_at,
    recalled: row.grade > 1,
    predicted: r,
  };
}

/** Words met in an activity at a time (from exercise, listen and recording rows). */
export interface Touch {
  userId: string;
  strategy: StrategyTag;
  lemmaIds: readonly string[];
  at: string;
}

/** The words an exercise or listen row touched (none for a card grade: the card is the yardstick). */
export function touchFromReview(row: ReviewLogRow): Touch | null {
  if (row.mode !== 'exercise' && row.mode !== 'shared') return null;
  const log = parse(row.log);
  const lemmas = Array.isArray(log.lemmas) ? log.lemmas.filter((x): x is string => typeof x === 'string') : [];
  if (!lemmas.length) return null;
  const strategy: StrategyTag | null = isActivityId(log.activity)
    ? strategyOf(log.activity)
    : log.kind === 'listen'
      ? 'listening'
      : typeof log.strategy === 'string'
        ? (log.strategy as StrategyTag)
        : null;
  if (!strategy) return null;
  return { userId: row.user_id, strategy, lemmaIds: lemmas, at: row.reviewed_at };
}

// ---------- the recording score summary ----------

/** The parts of the analyses the summary reads (engine/src/audio stress.ts, vowel-length.ts). */
export interface AnalysisLike {
  /** The stress check's words (`text`, `index`: the per-word results of stress-focus.ts). */
  stress?: { words: readonly { verdict: string; error?: string; text?: string; index?: number }[] } | undefined;
  vowelLength?:
    | {
        mode: string;
        syllables: readonly { long: boolean; verdict: string }[];
      }
    | undefined;
}

/** Counts of one analysed take; null when nothing could be judged (no row is written then). */
export function recordingSummary(
  a: AnalysisLike,
): Pick<RecordingScoreEntry, 'mode' | 'stressChecked' | 'stressOk' | 'stressInitial' | 'longChecked' | 'longOk'> | null {
  const words = a.stress?.words ?? [];
  const checked = words.filter((w) => w.verdict === 'ok' || w.verdict === 'wrong');
  const longs = (a.vowelLength?.syllables ?? []).filter((s) => s.long && s.verdict !== 'unsure');
  if (!checked.length && !longs.length) return null;
  const mode = a.vowelLength?.mode;
  return {
    mode: mode === 'reference' || mode === 'peterson' || mode === 'practice' ? mode : null,
    stressChecked: checked.length,
    stressOk: checked.filter((w) => w.verdict === 'ok').length,
    stressInitial: checked.filter((w) => w.verdict === 'wrong' && w.error === 'initial').length,
    longChecked: longs.length,
    longOk: longs.filter((s) => s.verdict === 'ok').length,
  };
}
